import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icons } from "assets/svgs";
import { EVIDENCE_STATUSES } from "utils/constants";

const DROPDOWN_POSITION_SHIFT = 12;

export const EvidenceStatusesSelect = ({ onChange, defaultValue, showLabel = false }) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [localValue, setLocalValue] = useState(defaultValue);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef();

  const handleClickOutside = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpenDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (isOpenDropdown) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpenDropdown, handleClickOutside]);

  const onSelect = (newValue) => {
    onChange(newValue);
    setLocalValue(newValue);
    setIsOpenDropdown(false);
  };

  const getBackgroundColor = () => {
    switch (localValue) {
      case 0:
        return "bg-yellow-400";
      case 1:
        return "bg-blue-500";
      case 2:
        return "bg-green-500";
      default:
        return "bg-green-500";
    }
  };

  const getScrollableParents = (el) => {
    const scrollables = [];
    let parent = el?.parentElement;

    while (parent && parent !== document.body) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollables.push(parent);
      }
      parent = parent.parentElement;
    }

    return scrollables;
  };

  const updateDropdownPosition = useCallback(() => {
    const rect = dropdownRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  }, []);

  useEffect(() => {
    if (!isOpenDropdown) return;

    updateDropdownPosition();

    const scrollParents = getScrollableParents(dropdownRef.current);
    scrollParents.forEach((el) => el.addEventListener("scroll", updateDropdownPosition));

    return () => {
      scrollParents.forEach((el) => el.removeEventListener("scroll", updateDropdownPosition));
    };
  }, [isOpenDropdown, updateDropdownPosition]);

  const selectedValueLabel = EVIDENCE_STATUSES.find((s) => s.value === localValue)?.label;

  return (
    <div
      className="flex items-center gap-x-2"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {showLabel && <p>Status:</p>}
      <div ref={dropdownRef} className="relative w-fit">
        <div
          className={`flex items-center justify-between p-2 rounded overflow-hidden text-white gap-x-4 cursor-pointer ${getBackgroundColor()}`}
          onClick={() => setIsOpenDropdown((prev) => !prev)}
        >
          <p className="">{selectedValueLabel}</p>
          <Icons.ArrowDownIcon />
        </div>
        {isOpenDropdown && (
          <div className="w-[112px] fixed z-10" style={dropdownPosition}>
            <ul className="flex flex-col rounded shadow-lg bg-white">
              {EVIDENCE_STATUSES.map((item) => {
                return (
                  <li
                    key={item.value}
                    className="p-2 cursor-pointer hover:bg-gray-100 text-left"
                    onClick={() => onSelect(item.value)}
                  >
                    {item.label}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
